import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { loc } from "../lib/localize";
import ArticleBody from "../components/ArticleBody";
import { AutoLink } from "../components/AutoLink";
import { setMeta } from "../lib/meta";
import Comments from "../components/Comments";
import ReactionBar from "../components/ReactionBar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Coins, Loader2, ThumbsUp, ThumbsDown, Users, Trophy } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function youtubeEmbedUrl(url) {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
}

export default function ArticlePage({ forceLang }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUser } = useAuth();
  const { t, translateError, lang, setLang } = useLanguage();
  const prevLang = useRef(lang);
  const [article, setArticle] = useState(null);
  const [titles, setTitles] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [myBet, setMyBet] = useState(null);

  const [betDialog, setBetDialog] = useState({ open: false, side: null });
  const [stake, setStake] = useState(10);
  const [placing, setPlacing] = useState(false);

  const [bettors, setBettors] = useState({ open: false, side: null, list: [], loading: false });

  const isAuthed = user && user !== false;

  // Force the page language when arriving via /en/wiki/:slug or /nl/wiki/:slug.
  useEffect(() => {
    if (forceLang && forceLang !== lang) setLang(forceLang);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceLang]);

  // Keep the URL in sync with the chosen language so the link is shareable.
  // Only reacts to an actual language change (not the initial mount).
  useEffect(() => {
    if (!article) return;
    if (prevLang.current === lang) return;
    prevLang.current = lang;
    const desired = lang === "en"
      ? `/en/wiki/${article.slug_en || article.slug}`
      : `/wiki/${article.slug}`;
    if (location.pathname !== desired) navigate(desired, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, article]);

  const loadMyBet = useCallback(async (articleId) => {
    if (!isAuthed) return;
    try {
      const { data } = await api.get("/bets/mine");
      const mine = data.find((b) => b.article_id === articleId);
      setMyBet(mine || null);
    } catch {}
  }, [isAuthed]);

  useEffect(() => {
    setArticle(null);
    setNotFound(false);
    setMyBet(null);
    api.get(`/articles/${slug}`)
      .then(({ data }) => { setArticle(data); loadMyBet(data.id); })
      .catch(() => setNotFound(true));
    api.get("/articles/titles").then(({ data }) => setTitles(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, user]);

  useEffect(() => {
    const SITE = "Probably True";
    if (notFound) {
      document.title = `${t("title.notFound")} — ${SITE}`;
      setMeta({ title: t("title.notFound") });
    } else if (article) {
      const title = loc(article, "title", lang);
      document.title = `${title} — ${SITE}`;
      setMeta({
        title,
        description: loc(article, "claim", lang) || loc(article, "summary", lang),
      });
    }
  }, [article, notFound, lang, t]);

  const sideLabel = (s) => (s === "agree" ? t("article.agree") : t("article.disagree"));

  const openBet = (side) => {
    if (!isAuthed) {
      navigate("/login", { state: { from: `/wiki/${slug}` } });
      return;
    }
    setStake(10);
    setBetDialog({ open: true, side });
  };

  const confirmBet = async () => {
    const amount = parseInt(stake, 10);
    if (!amount || amount < 1) return toast.error(t("article.invalidStake"));
    setPlacing(true);
    try {
      const { data } = await api.post("/bets", {
        article_id: article.id, stake: amount, side: betDialog.side,
      });
      setUser({ ...user, credits: data.credits });
      setArticle((a) => ({
        ...a,
        total_staked: a.total_staked + amount,
        bet_count: a.bet_count + 1,
        total_agree: a.total_agree + (betDialog.side === "agree" ? amount : 0),
        total_disagree: a.total_disagree + (betDialog.side === "disagree" ? amount : 0),
      }));
      setMyBet(data.bet);
      setBetDialog({ open: false, side: null });
      toast.success(t("article.betPlaced"));
    } catch (e) {
      toast.error(translateError(formatApiError(e.response?.data?.detail)));
    } finally {
      setPlacing(false);
    }
  };

  const openBettors = async (side) => {
    setBettors({ open: true, side, list: [], loading: true });
    try {
      const { data } = await api.get(`/articles/${article.id}/bettors`, { params: { side } });
      setBettors({ open: true, side, list: data, loading: false });
    } catch {
      setBettors({ open: true, side, list: [], loading: false });
    }
  };

  if (notFound) {
    return (
      <main className="relative z-10 mx-auto max-w-3xl px-6 py-24 text-center">
        <h1 className="font-heading text-3xl font-black">{t("article.notFound")}</h1>
        <Link to="/wiki" className="mt-6 inline-block text-primary hover:underline">{t("article.backToWiki")}</Link>
      </main>
    );
  }
  if (!article) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const noCredits = isAuthed && (user.credits || 0) <= 0;
  const sideMult = betDialog.side === "agree" ? article.agree_multiplier : article.disagree_multiplier;
  const payout = (parseInt(stake, 10) || 0) * (sideMult || 1);
  const localizedTitles = titles.map((tt) => ({
    slug: tt.slug,
    slug_en: tt.slug_en,
    to: lang === "en" ? `/en/wiki/${tt.slug_en || tt.slug}` : `/wiki/${tt.slug}`,
    terms: [loc(tt, "title", lang), ...((lang === "en" ? tt.synonyms_en : tt.synonyms_nl) || [])],
  }));

  return (
    <main className="relative z-10 mx-auto max-w-6xl px-6 py-12 md:px-10">
      <Link to="/wiki" data-testid="back-to-wiki" className="mb-8 inline-flex items-center gap-2 text-sm uppercase tracking-widest text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> {t("article.backWiki")}
      </Link>

      <div className="grid grid-cols-1 gap-12 lg:grid-cols-[1fr_340px]">
        <article className="animate-rise">
          <span className="font-mono text-xs uppercase tracking-widest text-primary">{loc(article, "category", lang)}</span>
          <h1 data-testid="article-title" className="mt-3 font-heading text-4xl font-black leading-none tracking-tighter sm:text-5xl">
            {loc(article, "title", lang)}
          </h1>
          {loc(article, "summary", lang) && (
            <p className="mt-6 border-l-2 border-primary pl-4 text-lg italic text-foreground/70"><AutoLink text={loc(article, "summary", lang)} currentSlug={article.slug} /></p>
          )}
          {article.image_url && (
            <img
              src={`${BACKEND_URL}${article.image_url}`}
              alt={loc(article, "title", lang)}
              data-testid="article-image"
              className="mt-8 max-h-[420px] w-full border border-white/10 object-cover"
            />
          )}
          {loc(article, "claim", lang) && (
            <div data-testid="central-claim" className="mt-8 border-l-2 border-credit bg-credit/5 p-5">
              <p className="font-mono text-[10px] uppercase tracking-widest text-credit">{t("article.claimLabel")}</p>
              <p className="mt-2 font-heading text-xl font-bold leading-snug text-foreground"><AutoLink text={loc(article, "claim", lang)} currentSlug={article.slug} /></p>
            </div>
          )}
          <div className="mt-10">
            <ArticleBody body={loc(article, "body", lang)} targets={localizedTitles} currentSlug={article.slug} />
          </div>
          {youtubeEmbedUrl(article.youtube_url) && (
            <div className="mt-10" data-testid="article-video">
              <p className="mb-3 font-mono text-[10px] uppercase tracking-widest text-primary">{t("article.videoLabel")}</p>
              <div className="relative w-full overflow-hidden border border-white/10" style={{ paddingTop: "56.25%" }}>
                <iframe
                  src={youtubeEmbedUrl(article.youtube_url)}
                  title={loc(article, "title", lang)}
                  className="absolute inset-0 h-full w-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
            </div>
          )}
          <div className="mt-8 flex items-center gap-3 border-t border-white/10 pt-6" data-testid="article-reactions">
            <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">{t("reactions.articlePrompt")}</span>
            <ReactionBar targetType="article" targetId={article.id} likes={article.likes} dislikes={article.dislikes} mine={article.my_reaction} testidPrefix="article" />
          </div>
        </article>

        <aside className="space-y-6 lg:sticky lg:top-24 lg:self-start">
          <div className="relative overflow-hidden border border-credit/40 bg-gradient-to-br from-[#141414] to-[#0A0A0A] p-6" data-testid="bet-box">
            <p className="text-center font-mono text-xs uppercase tracking-widest text-muted-foreground"><AutoLink text={t("article.betTitlePrefix")} currentSlug={article.slug} keyPrefix="betprefix" /></p>
            <p className="mb-6 mt-2 text-center font-heading text-lg font-black leading-snug text-credit"><AutoLink text={loc(article, "claim", lang) || t("article.claimLabel")} currentSlug={article.slug} keyPrefix="betclaim" /></p>

            {(
              <div className="space-y-4">
                <div className={`border ${myBet?.side === "agree" ? "border-truth" : "border-white/10"} bg-background p-4`}>
                  <div className="mb-3 flex items-center justify-between">
                    <span className="flex items-center gap-2 font-heading text-lg font-bold text-truth"><ThumbsUp className="h-5 w-5" /> {t("article.agree")}</span>
                    <span className="font-heading text-2xl font-black text-lie" data-testid="agree-multiplier">{article.agree_multiplier}×</span>
                  </div>
                  <button onClick={() => openBettors("agree")} data-testid="agree-total" className="mb-3 block w-full text-left font-mono text-xs text-muted-foreground hover:text-foreground">
                    <span className="text-credit">{article.total_agree}</span>{" "}
                    {t("article.stakedSuffix", { c: article.agree_count })}
                  </button>
                  <Button
                    onClick={() => openBet("agree")}
                    disabled={myBet && myBet.side === "disagree"}
                    data-testid="bet-agree-button"
                    className="w-full rounded-md bg-truth py-5 font-bold uppercase tracking-wider text-black hover:bg-truth/80 disabled:opacity-40"
                  >
                    {myBet?.side === "agree" ? t("article.increaseBet") : t("article.agree")}
                  </Button>
                </div>

                <div className={`border ${myBet?.side === "disagree" ? "border-lie" : "border-white/10"} bg-background p-4`}>
                  <div className="mb-3 flex items-center justify-between">
                    <span className="flex items-center gap-2 font-heading text-lg font-bold text-lie"><ThumbsDown className="h-5 w-5" /> {t("article.disagree")}</span>
                    <span className="font-heading text-2xl font-black text-truth" data-testid="disagree-multiplier">{article.disagree_multiplier}×</span>
                  </div>
                  <button onClick={() => openBettors("disagree")} data-testid="disagree-total" className="mb-3 block w-full text-left font-mono text-xs text-muted-foreground hover:text-foreground">
                    <span className="text-credit">{article.total_disagree}</span>{" "}
                    {t("article.stakedSuffix", { c: article.disagree_count })}
                  </button>
                  <Button
                    onClick={() => openBet("disagree")}
                    disabled={myBet && myBet.side === "agree"}
                    data-testid="bet-disagree-button"
                    variant="outline"
                    className="w-full rounded-md border-lie/50 bg-transparent py-5 font-bold uppercase tracking-wider text-lie hover:bg-lie/10 disabled:opacity-40"
                  >
                    {myBet?.side === "disagree" ? t("article.increaseBet") : t("article.disagree")}
                  </Button>
                </div>

                {!isAuthed && <p className="text-center text-xs text-muted-foreground">{t("article.loginToBet")}</p>}
                <p className="border-t border-white/10 pt-4 text-center text-sm italic text-foreground/70">
                  Put your money where your mouth is at <span className="text-credit not-italic">www.probablytrue.org</span>
                </p>
              </div>
            )}
          </div>

          {myBet && (
            <div data-testid="skin-in-the-game" className="relative overflow-hidden border border-credit/40 bg-gradient-to-br from-[#141414] to-[#0A0A0A] p-6 animate-rise">
              <div className="absolute right-4 top-4 opacity-20"><Trophy className="h-12 w-12 text-credit" /></div>
              <p className="font-heading text-xl font-black uppercase tracking-tight text-credit">Skin in the game</p>
              <div className="mt-4 border-t border-white/10 pt-4">
                <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{t("article.articleLabel")}</p>
                <p className="font-heading text-lg font-bold leading-tight">{loc(article, "title", lang)}</p>
                {loc(article, "claim", lang) && (
                  <>
                    <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-credit">{t("article.claimLabel")}</p>
                    <p className="font-heading text-base font-bold leading-snug text-foreground/90"><AutoLink text={loc(article, "claim", lang)} currentSlug={article.slug} /></p>
                  </>
                )}
                <div className="mt-3 flex items-center gap-2">
                  {myBet.side === "agree"
                    ? <ThumbsUp className="h-4 w-4 text-truth" />
                    : <ThumbsDown className="h-4 w-4 text-lie" />}
                  <span className={myBet.side === "agree" ? "text-truth" : "text-lie"}>{sideLabel(myBet.side)}</span>
                  <span className="text-muted-foreground">· {myBet.stake} {t("common.credits")}</span>
                </div>
                <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{t("article.bettorLabel")}</p>
                <p className="font-heading text-lg font-bold">{user.name}</p>
              </div>
              <p className="mt-5 border-t border-white/10 pt-4 text-sm italic text-foreground/70">
                Put your money where your mouth is at <span className="text-credit not-italic">www.probablytrue.org</span>
              </p>
            </div>
          )}
        </aside>
      </div>

      <div className="max-w-3xl">
        <Comments articleId={article.id} />
      </div>

      <Dialog open={betDialog.open} onOpenChange={(o) => setBetDialog((s) => ({ ...s, open: o }))}>
        <DialogContent className="max-w-md border-white/10 bg-card" data-testid="bet-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-black">{sideLabel(betDialog.side)}</DialogTitle>
            <DialogDescription>
              {sideMult > 1
                ? t("article.betDescAgree", { m: sideMult })
                : t("article.betDescDisagree")}
            </DialogDescription>
          </DialogHeader>

          {noCredits ? (
            <div className="py-4 text-center" data-testid="no-credits-message">
              <Coins className="mx-auto h-10 w-10 text-credit" />
              <p className="mt-4 text-foreground/80">{t("article.noCreditsMsg")}</p>
              <Button asChild className="mt-6 w-full rounded-md bg-primary font-bold uppercase tracking-wider hover:bg-[#FF645A]">
                <Link to="/credits" data-testid="buy-credits-cta">{t("article.buyCredits")}</Link>
              </Button>
            </div>
          ) : (
            <>
              <div className="py-2">
                <label className="mb-2 block font-mono text-xs uppercase tracking-widest text-muted-foreground">{t("article.howMuch")}</label>
                <Input
                  type="number" min="1" value={stake}
                  onChange={(e) => setStake(e.target.value)}
                  data-testid="bet-stake-input"
                  className="border-white/15 bg-background text-lg"
                />
                <div className="mt-4 flex items-center justify-between border-t border-white/10 pt-4">
                  <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">{t("article.possiblePayout")}</span>
                  <span className="font-heading text-2xl font-black text-credit" data-testid="bet-payout-preview">{payout.toFixed(0)}</span>
                </div>
                <p className="mt-2 font-mono text-[11px] text-muted-foreground">{t("article.balanceCredits", { c: user?.credits })}</p>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setBetDialog({ open: false, side: null })} className="border-white/15 bg-transparent">{t("common.cancel")}</Button>
                <Button onClick={confirmBet} disabled={placing} data-testid="confirm-bet-button" className="rounded-md bg-primary font-bold uppercase tracking-wider hover:bg-[#FF645A]">
                  {placing ? <Loader2 className="h-4 w-4 animate-spin" /> : t("article.placeBet")}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={bettors.open} onOpenChange={(o) => setBettors((s) => ({ ...s, open: o }))}>
        <DialogContent className="max-w-md border-white/10 bg-card" data-testid="bettors-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading text-2xl font-black">
              <Users className="h-5 w-5 text-credit" /> Skin in the game
            </DialogTitle>
            <DialogDescription>{t("article.bettorsSub", { side: bettors.side ? sideLabel(bettors.side) : "" })}</DialogDescription>
          </DialogHeader>
          <div className="max-h-80 overflow-y-auto py-2">
            {bettors.loading ? (
              <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
            ) : bettors.list.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">{t("article.noBettors")}</p>
            ) : (
              <ul className="divide-y divide-white/5">
                {bettors.list.map((b, i) => (
                  <li key={i} className="flex items-center justify-between py-3" data-testid="bettor-row">
                    <span className={`font-medium ${b.anonymous ? "text-muted-foreground italic" : ""}`}>{b.name}</span>
                    <span className="inline-flex items-center gap-1 font-mono text-sm text-credit"><Coins className="h-3 w-3" />{b.stake}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <p className="border-t border-white/10 pt-4 text-center text-sm italic text-foreground/70">
            These people put their money where their mouth is!
          </p>
        </DialogContent>
      </Dialog>
    </main>
  );
}
